import React, { useRef, useEffect } from 'react';
import useStore from '../../../store';
import './CanvasPuzzleBoard.css';

// --- Новые и старые хуки ---
// import useCanvasSetup from '../hooks/useCanvasSetup'; // Удаляем
import useLayoutManager from '../hooks/useLayoutManager'; // Добавляем
import usePuzzlePointer from '../hooks/usePuzzlePointer';
import useCanvasRenderer from '../hooks/useCanvasRenderer';
import usePcZoom from '../hooks/usePcZoom'; // Оставляем для зума/пана
import usePhoneZoom from '../hooks/usePhoneZoom'; // Оставляем для зума/пана

// Импортируем компоненты
import DebugMenu from './debug/DebugMenu';
import DebugInfo from './debug/DebugInfo';
import TrayPieces from './pieces/TrayPieces';
import DraggingPiece from './pieces/DraggingPiece';
import HighlightedPieces from './pieces/HighlightedPieces';
import ReservedPieces from './pieces/ReservedPieces';
import ChatOverlay from '../../board/indicators/ChatOverlay';
import StartAnimation from './pieces/StartAnimation'; // Оставляем импорт StartAnimation
import PuzzleTitle from './PuzzleTitle';

// Основной компонент-контейнер
export function CanvasPuzzleBoard() {

  const puzzleRender = useStore(s => s.puzzleRender); // Следим за puzzleRender для обновления динамических элементов типа DraggingPiece


  const boardCanvasRef = useRef(null); // Ref для канваса доски
  const trayCanvasRef = useRef(null); // Ref для канваса трея

  // Получаем доступ к store и ref один раз
  const store = useStore.getState();
  const { ref } = store;
  const { puzzle, info, telegram, menu, work } = ref;
  const { config, state: puzzleState, methods: puzzleMethods, startAnimation, boardTransform, boardCanvas } = puzzle; // Деструктурируем для удобства

  // Подписываемся на нужные состояния Zustand для ре-рендера компонента
  
  const isInitialized = puzzleState.isInitialized; // <<< Подписка на isInitialized
  const hasImageDimensions = puzzleState.imageDimensions?.width > 0; // <<< Подписка на imageDimensions
  const isLoading = puzzleState.isLoading; // <<< Подписка на isLoading

  // --- Используем хуки ---
  useLayoutManager(boardCanvasRef, trayCanvasRef); // <<< Новый хук для управления лейаутом
  usePuzzlePointer(boardCanvasRef, trayCanvasRef); // Для обработки кликов/тачей
  useCanvasRenderer(); // Для отрисовки канвасов
  usePcZoom(boardCanvasRef, trayCanvasRef); // Для зума/пана доски мышкой, передаем оба рефа
  usePhoneZoom(boardCanvasRef, trayCanvasRef); // Для зума/пана доски на смартфоне, передаем оба рефа
  // useCanvasSetup больше не нужен

  // Эффект для инициализации игры и темы (остается)
  useEffect(() => {
    if (!puzzleState.isInitialized && !puzzleState.isLoading) {
      puzzleMethods.initializePuzzle();
    }
    puzzleMethods.initTheme();

  }, [puzzleMethods, puzzleState.isInitialized, puzzleState.isLoading, menu.methods]); // Обновленные зависимости

  // --- Расчет заголовка (без изменений) ---
  const gameSettings = info.game?.settings;
  const gameAuthor = info.game?.author;
  const defaultTitle = `Puzzle by ${gameAuthor?.first_name || 'Unknown Author'}`;
  const puzzleName = gameSettings?.title || defaultTitle;
  const isDarkTheme = config.isDarkTheme;

  // --- Логика показа стартовой анимации (Упрощаем обратно) ---
  // Теперь StartAnimation сам будет решать, когда он готов и когда запускать логику
  const showStartAnimation =
    config.user?.startAnimation &&
    !startAnimation.finished &&
    puzzleState.isInitialized && 
    puzzleState.finalLayout && 
    config.game.puzzlePieceCount <= 500 && // Не для больших пазлов
    Date.now() < puzzleState.resetTime + 10 * 1000; // Не слишком поздно после сброса

  const debugMode = info.user.debugMode;

  return (
    <div className={`canvas-puzzle-container ${debugMode ? 'debug' : ''}`}>
      <PuzzleTitle
        puzzleName={puzzleName}
        isDarkTheme={isDarkTheme}
        onThemeToggle={() => puzzleMethods.toggleTheme()}
        onTitleClick={() => menu.methods.openMenu('puzzleSettings')}
      />

      {/* Контейнер для центрирования канваса доски */}

      { isInitialized && hasImageDimensions && !isLoading && (

        <>

          <div className={`puzzle-board-container ${debugMode ? 'debug' : ''}`} style={{
            top: `${puzzle.layout.boardContainer.top}px`,
            left: `${puzzle.layout.boardContainer.left}px`,
            width: `${puzzle.layout.boardContainer.width}px`,
            height: `${puzzle.layout.boardContainer.height}px`,
          }}>
            {/* Канвас для доски */}
            
            <canvas
              id="puzzle-canvas"
              ref={boardCanvasRef}
              className={`puzzle-board ${debugMode ? 'debug' : ''}`}
              tabIndex={0} // Для фокуса и событий клавиатуры (если нужно)
              style={{
                // Стиль для трансформации (зум/пан) остается
                transform: `translate(${boardCanvas.layout.x + boardTransform.x}px, ${boardCanvas.layout.y + boardTransform.y}px) scale(${boardTransform.scale})`,
                transformOrigin: 'top left',
              }}
            />
          </div>


          <div className={`puzzle-tray-container touch-active ${debugMode ? 'debug' : ''}`} style={{
            top: `${puzzle.layout.trayContainer.top}px`,
            left: `${puzzle.layout.trayContainer.left}px`,
            width: `${puzzle.layout.trayContainer.width}px`,
            height: `${puzzle.layout.trayContainer.height}px`,
            opacity: puzzle.config.showTray ? 1 : 0,
          }}
          >
            <TrayPieces puzzleRender={puzzleRender} />
          </div>

          <ReservedPieces />

          <DraggingPiece />
          <HighlightedPieces />
          {showStartAnimation && <StartAnimation />}
          {['141015727', '1431457632'].includes(String(telegram.activeUserId)) && (
            <DebugMenu />
          )}
        </>
      )}


      {info.user.debugInfo && <DebugInfo />}
      <ChatOverlay store={store} /> {/* Передаем store, если он там нужен */}
    </div>
  );
}